import React, { useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { stripUnit } from 'polished'

import { INITIAL_ENGRAVING } from 'utils/constants'

import { media, mediaDown } from 'styles/media'
import { greyDark, white } from 'styles/colors'
import { animationTime, animationCurve } from 'styles/global'
import * as spacing from 'styles/spacing'
import { fontWeights, fontSizes } from 'styles/typography'
import { EngravingContext } from 'components/buy/Engraving/EngravingContext'
import CheckoutContext from 'contexts/Checkout'
import Button from 'components/button/Button'
import { ArrowButton } from 'components/buy/BuySummary'
import Icon from 'components/Icon'

import LeftArrow from 'icons/left-arrow.svg'

export const summaryHeightMobile = '78px'
export const summaryHeightDesktop = '90px'

const Container = styled.div`
  position: relative;
  width: 100%;
  z-index: 600;
`

const Main = styled.div`
  position: static;
`

const Inner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${summaryHeightMobile};
  padding: 0 ${stripUnit(spacing.medium) * 1.25 + 'px'};
  background-color: ${white};

  ${media.medium`
    flex-direction: row-reverse;
    height: ${summaryHeightDesktop};
    padding: 0 ${spacing.large};
  `};

  ${media.large`
    padding: 0 ${spacing.xLarge};
  `};

  ${media.xLarge`
    padding: 0 ${spacing.xxLarge};
  `};
`

export const Skip = styled.button`
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  transition: color ${animationTime} ${animationCurve};
  cursor: pointer;

  ${mediaDown.medium`
    text-decoration: underline;
  `};

  &:hover {
    color: ${greyDark};
  }

  ${media.medium`
    display: flex;
    align-items: center;
    margin-left: ${spacing.large};
  `};

  ${props =>
    props.disabled &&
    css`
      opacity: 0.3;
      cursor: default;
    `}
`

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${media.medium`
    flex-direction: row;
    align-items: center;
  `};
`

const ButtonOuter = styled.div`
  right: ${stripUnit(spacing.medium) * 1.25 + 'px'};

  ${media.small`
    right: ${spacing.large};
  `};

  ${mediaDown.medium`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  `};

  ${media.medium`
    right: auto;
  `};
`

const EngravingSummary = () => {
  const [itemsLength, setItemsLength] = useState(0)
  const [showCancelButton, setShowCancelButton] = useState(true)
  const { engraving, setEngraving, proceedItem, skipItem, currentView, resetEngraving, setVisible } = useContext(
    EngravingContext
  )
  const { loading } = useContext(CheckoutContext)
  const { item, step } = currentView

  useEffect(() => {
    const items = document.getElementsByClassName('summary-item')
    setItemsLength(items.length)
  })

  useEffect(() => {
    if (item === 'box') {
      const items = ['front', 'back']

      items.forEach(item => {
        const steps = Object.keys(engraving[item]).filter(key => typeof engraving[item][key] === 'object')
        const engravedSteps = steps.filter(step => engraving[item][step].value.length > 0)
        if (engravedSteps.length > 0) setShowCancelButton(false)
      })
    }
  }, [item])

  let disabled = false || loading

  if (step !== 'initialstep') {
    disabled = !engraving[item][step].value.length

    if (step === 'text' && item !== 'box') {
      disabled = disabled || !engraving[item][step].font.length
    }
  }

  return (
    <>
      <Container>
        <Main>
          <Inner>
            <ButtonWrap>
              {currentView.item === 'front' && step === 'initialstep' && (
                <ArrowButton
                  onClick={() => {
                    resetEngraving()
                  }}
                  className="u-hide-medium-up"
                >
                  <Icon icon={<LeftArrow />} />
                </ArrowButton>
              )}
              {step !== 'initialstep' && (
                <Button
                  theme="secondary"
                  onClick={() => {
                    proceedItem(item, step)
                  }}
                  disabled={disabled}
                >
                  {`Add ${engraving[item][step]?.displayName}`}
                </Button>
              )}
              {item === 'summary' && (
                <>
                  {itemsLength === 0 ? (
                    <Button
                      theme="secondary"
                      onClick={() => {
                        setEngraving(INITIAL_ENGRAVING)
                      }}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <>
                      <ButtonWrap>
                        <Button
                          theme="secondary"
                          onClick={() => {
                            setEngraving({ ...engraving, proceeded: true })
                            setVisible(false)
                          }}
                          disabled={disabled}
                        >
                          Add engraving
                        </Button>
                        <ButtonOuter>
                          <Skip
                            onClick={() => {
                              resetEngraving()
                            }}
                            disabled={disabled}
                          >
                            Cancel
                          </Skip>
                        </ButtonOuter>
                      </ButtonWrap>
                    </>
                  )}
                </>
              )}
              {item !== 'summary' && (
                <>
                  {item === 'box' && showCancelButton ? (
                    <ButtonOuter>
                      <Skip
                        onClick={() => {
                          resetEngraving()
                        }}
                      >
                        Cancel
                      </Skip>
                    </ButtonOuter>
                  ) : (
                    <ButtonOuter>
                      <Skip
                        onClick={() => {
                          skipItem(item)
                        }}
                      >
                        {currentView.item === 'front' && <>Skip to customize back</>}
                        {currentView.item === 'back' && <>Skip to customize box</>}
                        {currentView.item === 'box' && <>Skip to review</>}
                      </Skip>
                    </ButtonOuter>
                  )}
                </>
              )}
            </ButtonWrap>
          </Inner>
        </Main>
      </Container>
    </>
  )
}

export default EngravingSummary
